/**
 * Chart kit (dataviz-method compliant, light surface):
 *  - Sparkline: single-series line + hover crosshair (E2 small multiples)
 *  - RangeBand: low/high band over time with direct note labels (E3)
 *  - Ring: single-value completion meter (C9)
 * Palette: brand-led validated categorical slots — series-1 terracotta
 * #d85a30 (the logo hue), series-2 blue #2a78d6 (validated pair, CVD ΔE 23).
 */

import { useId, useMemo, useState } from 'react';
import { midiToName } from '@solfo/shared';

export const SERIES_1 = '#d85a30';
export const SERIES_2 = '#2a78d6';
const GRID = '#e8e2db';
const TEXT_SECONDARY = '#5c554e';

interface Pt {
  x: number; // epoch ms
  y: number;
}

function scale(domain: [number, number], range: [number, number]) {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0 || 1;
  return (v: number) => r0 + ((v - d0) / span) * (r1 - r0);
}

export function Sparkline({
  points,
  height = 96,
  yDomain,
  unit = '',
}: {
  points: Pt[];
  height?: number;
  yDomain?: [number, number];
  unit?: string;
}) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const W = 320;
  const PAD = { l: 8, r: 8, t: 10, b: 6 };
  const data = useMemo(() => [...points].sort((a, b) => a.x - b.x), [points]);
  if (data.length === 0) return <p className="text-xs text-slate-400">No data yet</p>;

  const xs = data.map((p) => p.x);
  const ys = data.map((p) => p.y);
  const dom: [number, number] = yDomain ?? [Math.min(...ys) - 5, Math.max(...ys) + 5];
  const sx = scale([Math.min(...xs), Math.max(...xs)], [PAD.l, W - PAD.r]);
  const sy = scale(dom, [height - PAD.b, PAD.t]);
  const path = data.map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(' ');
  const h = hover !== null ? data[hover] : null;

  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      className="w-full touch-none"
      role="img"
      aria-labelledby={id}
      onPointerMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const px = ((e.clientX - rect.left) / rect.width) * W;
        let best = 0;
        let bestD = Infinity;
        data.forEach((p, i) => {
          const d = Math.abs(sx(p.x) - px);
          if (d < bestD) {
            bestD = d;
            best = i;
          }
        });
        setHover(best);
      }}
      onPointerLeave={() => setHover(null)}
    >
      <title id={id}>Score over time</title>
      {[dom[0], (dom[0] + dom[1]) / 2, dom[1]].map((gy) => (
        <line key={gy} x1={PAD.l} x2={W - PAD.r} y1={sy(gy)} y2={sy(gy)} stroke={GRID} strokeWidth="1" />
      ))}
      <path d={path} fill="none" stroke={SERIES_1} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {/* direct label on the latest value — selective, not every point */}
      {(() => {
        const last = data[data.length - 1]!;
        return (
          <text x={Math.min(sx(last.x), W - 30)} y={sy(last.y) - 6} fontSize="11" fill={TEXT_SECONDARY} textAnchor="end" fontWeight={600}>
            {Math.round(last.y)}
            {unit}
          </text>
        );
      })()}
      {h && (
        <g>
          <line x1={sx(h.x)} x2={sx(h.x)} y1={PAD.t} y2={height - PAD.b} stroke="#a89e94" strokeDasharray="3 3" strokeWidth="1" />
          <circle cx={sx(h.x)} cy={sy(h.y)} r="4" fill={SERIES_1} stroke="#fff" strokeWidth="2" />
          <g transform={`translate(${Math.min(Math.max(sx(h.x) - 40, 2), W - 84)}, 0)`}>
            <rect width="82" height="18" rx="4" fill="#2c2c2a" opacity="0.9" />
            <text x="41" y="12.5" fontSize="10" fill="#fff" textAnchor="middle">
              {new Date(h.x).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} · {Math.round(h.y)}
              {unit}
            </text>
          </g>
        </g>
      )}
    </svg>
  );
}

export function RangeBand({
  history,
  height = 160,
}: {
  history: { date: string; lowMidi: number; highMidi: number }[];
  height?: number;
}) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const W = 360;
  const PAD = { l: 34, r: 44, t: 12, b: 18 };
  const data = useMemo(
    () => history.map((h) => ({ x: new Date(h.date).getTime(), low: h.lowMidi, high: h.highMidi })),
    [history],
  );
  if (data.length === 0) return <p className="text-xs text-slate-400">Run a range test to start this chart.</p>;

  const xs = data.map((d) => d.x);
  const lows = data.map((d) => d.low);
  const highs = data.map((d) => d.high);
  const dom: [number, number] = [Math.min(...lows) - 2, Math.max(...highs) + 2];
  const sx = scale([Math.min(...xs), Math.max(...xs) || Math.min(...xs) + 1], [PAD.l, W - PAD.r]);
  const sy = scale(dom, [height - PAD.b, PAD.t]);

  const bandPath =
    data.map((d, i) => `${i === 0 ? 'M' : 'L'}${sx(d.x).toFixed(1)},${sy(d.high).toFixed(1)}`).join(' ') +
    [...data].reverse().map((d) => `L${sx(d.x).toFixed(1)},${sy(d.low).toFixed(1)}`).join(' ') +
    'Z';
  const last = data[data.length - 1]!;
  const gridNotes: number[] = [];
  for (let m = Math.ceil(dom[0] / 12) * 12; m <= dom[1]; m += 12) gridNotes.push(m); // octave Cs
  const h = hover !== null ? data[hover] : null;

  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      className="w-full touch-none"
      role="img"
      aria-labelledby={id}
      onPointerMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const px = ((e.clientX - rect.left) / rect.width) * W;
        let best = 0;
        let bestD = Infinity;
        data.forEach((p, i) => {
          const d = Math.abs(sx(p.x) - px);
          if (d < bestD) {
            bestD = d;
            best = i;
          }
        });
        setHover(best);
      }}
      onPointerLeave={() => setHover(null)}
    >
      <title id={id}>Vocal range development</title>
      {gridNotes.map((m) => (
        <g key={m}>
          <line x1={PAD.l} x2={W - PAD.r} y1={sy(m)} y2={sy(m)} stroke={GRID} strokeWidth="1" />
          <text x={PAD.l - 4} y={sy(m) + 3} fontSize="10" fill={TEXT_SECONDARY} textAnchor="end">
            {midiToName(m)}
          </text>
        </g>
      ))}
      <path d={bandPath} fill={SERIES_1} opacity="0.14" />
      <path
        d={data.map((d, i) => `${i === 0 ? 'M' : 'L'}${sx(d.x).toFixed(1)},${sy(d.high).toFixed(1)}`).join(' ')}
        fill="none"
        stroke={SERIES_1}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d={data.map((d, i) => `${i === 0 ? 'M' : 'L'}${sx(d.x).toFixed(1)},${sy(d.low).toFixed(1)}`).join(' ')}
        fill="none"
        stroke={SERIES_1}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* direct labels: current extremes */}
      <text x={sx(last.x) + 6} y={sy(last.high) + 4} fontSize="11" fontWeight={600} fill={TEXT_SECONDARY}>
        {midiToName(last.high)}
      </text>
      <text x={sx(last.x) + 6} y={sy(last.low) + 4} fontSize="11" fontWeight={600} fill={TEXT_SECONDARY}>
        {midiToName(last.low)}
      </text>
      {h && (
        <g>
          <line x1={sx(h.x)} x2={sx(h.x)} y1={PAD.t} y2={height - PAD.b} stroke="#a89e94" strokeDasharray="3 3" strokeWidth="1" />
          <circle cx={sx(h.x)} cy={sy(h.high)} r="4" fill={SERIES_1} stroke="#fff" strokeWidth="2" />
          <circle cx={sx(h.x)} cy={sy(h.low)} r="4" fill={SERIES_1} stroke="#fff" strokeWidth="2" />
          <g transform={`translate(${Math.min(Math.max(sx(h.x) - 55, 2), W - 112)}, 0)`}>
            <rect width="110" height="18" rx="4" fill="#2c2c2a" opacity="0.9" />
            <text x="55" y="12.5" fontSize="10" fill="#fff" textAnchor="middle">
              {new Date(h.x).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} · {midiToName(h.low)}–{midiToName(h.high)}
            </text>
          </g>
        </g>
      )}
    </svg>
  );
}

export function Ring({
  value,
  label,
  size = 92,
  sub,
}: {
  value: number; // 0..1
  label: string;
  size?: number;
  sub?: string;
}) {
  const r = size / 2 - 7;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, value));
  return (
    <div className="flex flex-col items-center" role="img" aria-label={`${label}: ${Math.round(v * 100)}%`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={GRID} strokeWidth="9" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={SERIES_1}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={`${c * v} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={size / 4.6} fontWeight="700" fill="#2c2c2a">
          {Math.round(v * 100)}%
        </text>
      </svg>
      <span className="mt-1 text-xs font-medium text-slate-600">{label}</span>
      {sub && <span className="text-[11px] text-slate-400">{sub}</span>}
    </div>
  );
}
